/**
 * The two things every screen needs, fetched once.
 *
 * The registry describes what BEST can edit and changes only when the tool
 * itself changes, so it is loaded at start-up and treated as immutable for the
 * life of the page. The principal decides what the person may do with it.
 *
 * This is the whole of BEST's global state. There is deliberately no store:
 * everything else belongs to the screen that asked for it, and the API is the
 * source of truth for all of it.
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { ApiError, api, onGitLabUnreachable, onUnauthorized } from '../api'
import { viewedBaseline } from './baselineRef'
import { useProject } from './Project'
import type { Schema } from '../schema'

export interface Principal {
  name: string
  role: string
  method: string
  anonymous: boolean
  can_write: boolean
  /**
   * This BEST cannot reach GitLab, so there is no branch to protect.
   *
   * `best serve --sandbox`. Nothing publishes and the database is the record,
   * which is why the default branch is writable here and read-only everywhere
   * else. Optional: an older server does not send it, and absent must mean
   * "not a sandbox" rather than "assume one".
   */
  sandbox?: boolean
  /**
   * The role in the BEST project itself, which governs what is not one
   * program's model (registering programs). Null for somebody who belongs to a
   * program's GitLab project and not to BEST's. Absent from an older server.
   */
  install_role?: string | null
  /** The program `role` and `can_write` describe. */
  program?: string | null
  /** The role in that program's own GitLab project. */
  program_role?: string | null
  /**
   * The tag of the baseline being viewed, or null (issue #97, phase D). While one
   * is, `can_write` is false. Absent from an older server, and then no baseline.
   */
  baseline?: string | null
  /**
   * False while GitLab is not responding: the role above may be its last answer
   * replayed, and saving is paused. Absent from an older server, and then true.
   */
  gitlab_reachable?: boolean
}

/** How often to ask again while GitLab is not responding. */
export const GITLAB_RECHECK_MS = 45_000

/**
 * The role that governs the install rather than a program.
 *
 * `role` answers for the program being looked at, so using it to decide who may
 * register a program would let an admin of one program's GitLab project do
 * what only an admin of BEST's may. An older server sends no `install_role`,
 * and then `role` is the only answer there is.
 */
export function installRole(principal: Principal): string | null {
  return principal.install_role === undefined ? principal.role : principal.install_role
}

interface AppData {
  schema: Schema
  principal: Principal
  /** Re-read the principal, e.g. after signing in. */
  refresh: () => Promise<void>
  /**
   * Re-read only the principal.
   *
   * What a person may do is decided PER PROGRAM, so choosing another program
   * can change their role and with it every control that depends on it.
   */
  refreshPrincipal: () => Promise<void>
}

const AppDataContext = createContext<AppData | null>(null)

interface LoadState {
  schema: Schema | null
  principal: Principal | null
  error: ApiError | null
}

/**
 * What the shell shows when the API will not answer without a principal.
 *
 * A 401 rather than a message of its own, because the sign-in screen reads the
 * status: 401 is the one case where there is something to do about it.
 */
const SIGNED_OUT = new ApiError(
  401,
  'Sign in to use BEST.',
  'Unauthorized',
)

export function AppDataProvider({
  children,
  fallback,
  onError,
}: {
  children: ReactNode
  /** Shown while the first load is in flight. */
  fallback?: ReactNode
  /** Rendered instead of the app when the first load fails. */
  onError?: (error: ApiError, retry: () => void) => ReactNode
}) {
  const [state, setState] = useState<LoadState>({
    schema: null,
    principal: null,
    error: null,
  })

  const load = () => {
    setState((previous) => ({ ...previous, error: null }))
    return Promise.all([api.schema(), api.whoami()])
      .then(([schema, principal]) => setState({ schema, principal, error: null }))
      .catch((error: ApiError) =>
        setState({ schema: null, principal: null, error }),
      )
  }

  useEffect(() => {
    void load()
    // Once. The registry does not change while the page is open.
  }, [])

  const refreshPrincipal = useCallback(
    () =>
      api
        .whoami()
        .then((principal) =>
          setState((previous) => (previous.principal ? { ...previous, principal } : previous)),
        )
        .catch(() => undefined),
    [],
  )

  /*
   * GitLab not responding, from either end: a response that says so (a save
   * that failed, a read refused) re-reads who we are straight away, and while
   * the answer is "not responding" it is asked again on an interval until GitLab
   * answers. Only while it is down - a healthy session polls nothing.
   */
  useEffect(() => onGitLabUnreachable(() => void refreshPrincipal()), [refreshPrincipal])

  const reachable = state.principal?.gitlab_reachable !== false
  useEffect(() => {
    if (reachable) return undefined
    const timer = setInterval(() => void refreshPrincipal(), GITLAB_RECHECK_MS)
    return () => clearInterval(timer)
  }, [reachable, refreshPrincipal])

  /*
   * Any 401, from anywhere, puts the sign-in screen back up.
   *
   * BEST refuses an unauthenticated caller on every route that carries model
   * data, so there is no degraded read-only state to fall back to: a session
   * that runs out leaves a page that can no longer load anything. Showing one
   * screen that says so, with the way back on it, beats each panel failing on
   * its own with an error nobody can act on.
   */
  useEffect(() => onUnauthorized(() => {
    setState({ schema: null, principal: null, error: SIGNED_OUT })
  }), [])

  if (state.error) {
    return <>{onError ? onError(state.error, () => void load()) : null}</>
  }
  if (!state.schema || !state.principal) {
    return <>{fallback ?? null}</>
  }

  return (
    <AppDataContext.Provider
      value={{
        schema: state.schema,
        principal: state.principal,
        refresh: () => load(),
        refreshPrincipal,
      }}
    >
      {children}
    </AppDataContext.Provider>
  )
}

function useAppData(): AppData {
  const value = useContext(AppDataContext)
  if (!value) {
    throw new Error('useAppData must be used inside an AppDataProvider')
  }
  return value
}

/** What BEST can edit. Never null inside the app - the shell waits for it. */
export function useSchema(): Schema {
  return useAppData().schema
}

/**
 * Re-read the schema and the principal.
 *
 * The schema is loaded ONCE at start-up, which is right for a description of
 * what BEST can edit - until something changes it. Declaring a requirement type
 * does: the type is discovered from the model, so it appears in /api/schema and
 * nowhere else, and without this it stays invisible in every picker until the
 * page is reloaded.
 */
export function useRefreshAppData(): () => Promise<void> {
  return useAppData().refresh
}

/** Who is signed in, and what they may do. */
export function usePrincipal(): Principal {
  return useAppData().principal
}

/** Re-read the principal alone, after the program being worked in changes. */
export function useRefreshPrincipal(): () => Promise<void> {
  return useAppData().refreshPrincipal
}

/**
 * Whether the signed-in person may change the model.
 *
 * Two rules, and a control is only live when both pass. GitLab access decides
 * whether this PERSON may write anything; the selected branch decides whether
 * THIS model may be written to, because BEST refuses every save on a program's
 * default branch - it is what everything merges into, so work happens on a
 * branch cut from an issue. Judging on the role alone left a Developer reading
 * main with an enabled Save button and a refusal waiting for them at the end
 * of the form.
 *
 * Used to DISABLE controls rather than hide them: a missing button is
 * indistinguishable from a broken page, whereas a disabled one with a reason
 * answers the question the person actually has.
 */
export function useCanWrite(): { allowed: boolean; reason: string; paused?: boolean } {
  const principal = usePrincipal()
  const { current, branch } = useProject()

  // Access is settled before the branch, deliberately. Somebody anonymous on
  // main fails both rules, and only one of the two answers names a step they
  // can take: starting work needs a GitLab token, so "start work on an issue"
  // would send them to a control that is itself disabled. Signing in is the
  // move that is available, and it is the one that unlocks the other.
  //
  // Anonymous first rather than can_write first: the two are never both true
  // - BEST's anonymous caller is always a reader - and asking who they are
  // before asking what they may do is the order the answers read in.
  if (principal.anonymous) {
    return { allowed: false, reason: 'Sign in with GitLab to make changes' }
  }
  // A BASELINE IS A FIXED COPY OF A TAG, whoever the person is. Asked before
  // can_write and before the sandbox exemption: the server already answers
  // can_write false while one is viewed, but a screen holding an older principal
  // would otherwise say "your GitLab access is read-only", which is untrue and
  // sends the person to the wrong fix. The way out is the banner's, not an issue.
  const baselineTagViewed = viewedBaseline(principal.baseline, branch)
  if (baselineTagViewed) {
    return {
      allowed: false,
      reason: `This is a read-only baseline (${baselineTagViewed}), a fixed copy of a tag. Go back to ${current?.default_branch || 'main'} to make changes.`,
    }
  }
  if (!principal.can_write) {
    return {
      allowed: false,
      reason: `Your GitLab access (${principal.role}) is read-only. Developer or above may change the model.`,
    }
  }

  // No registry entry is single-database mode: BEST was started against an
  // explicit database, so there is no repository behind the model and no
  // branch to protect. Nothing is resolved during the first load either, but
  // ProjectGate holds every screen back until it is.
  if (!current) return { allowed: true, reason: '' }

  // A SANDBOX HAS NO BRANCH TO PROTECT. `best serve --sandbox` cannot reach
  // GitLab at all, so nothing publishes and the database is the record - and
  // the server says so by returning from attach_publisher before it reaches the
  // default-branch rule below.
  //
  // This copy of that rule had no copy of the exemption, so a sandbox served a
  // UI with every editor disabled and a server that would have accepted the
  // change. Clicking around a local instance is the entire point of the mode.
  if (principal.sandbox) return { allowed: true, reason: '' }

  // GITLAB IS NOT RESPONDING. Every save is a commit, so none can be made, and
  // the server would refuse it anyway - say so here, once, rather than letting
  // each button fail. `paused` lets a form keep its fields editable: the edits
  // are the person's, they are kept, and they can go on making them.
  if (principal.gitlab_reachable === false) {
    return {
      allowed: false,
      paused: true,
      reason:
        'GitLab is not responding, so saving is paused. Your edits stay on the page; saving works again as soon as GitLab answers.',
    }
  }

  // Mirrors the server's own fallbacks: an unset branch is whatever it would
  // have chosen, and that is the default branch.
  const target = current.default_branch || 'main'
  if ((branch || target) === target) {
    return {
      allowed: false,
      reason: `${target} is read-only. It is the branch everything merges into, so start work on an issue in the bar above to make changes.`,
    }
  }

  return { allowed: true, reason: '' }
}
